import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Check, X } from "lucide-react";

const dt = (s) => (s ? new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Kolkata" }) : "—");
const typeCls = { showroom: "bg-blue-100 text-blue-700", home: "bg-emerald-100 text-emerald-700", connected: "bg-purple-100 text-purple-700", utility: "bg-slate-200 text-slate-700" };
const statusCls = { pending_sm_approval: "bg-amber-100 text-amber-700", blocked: "bg-blue-100 text-blue-700", approved: "bg-emerald-100 text-emerald-700", completed: "bg-slate-100 text-slate-600", rejected: "bg-red-100 text-red-700", expired: "bg-slate-100 text-slate-500", released: "bg-slate-100 text-slate-500" };

function HandoverApprovals() {
  const [items, setItems] = useState([]);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");
  const load = () => api.get("/handovers", { params: { status: "pending_vp_approval" } }).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);
  const approve = async (id) => {
    try { await api.post(`/handovers/${id}/approve`); toast.success("Approved — vehicle moved to Accounts, pending disposal"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const reject = async () => {
    if (!reason.trim()) { toast.error("A rejection reason is required"); return; }
    try { await api.post(`/handovers/${rejecting}/reject`, { reason }); toast.success("Handover rejected"); setRejecting(null); setReason(""); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-4 space-y-3">
      <h2 className="font-heading text-xs font-bold uppercase tracking-widest text-slate-400">Handovers to Accounts (end of life)</h2>
      {items.length === 0 && <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-center text-sm font-semibold text-emerald-700">No pending handovers</div>}
      {items.map((h) => (
        <div key={h.id} data-testid={`handover-card-${h.reg_no}`} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="font-heading font-bold text-[#0E2240]">{h.reg_no} · {h.model}</div>
              <div className="mt-0.5 text-xs text-slate-500">{h.branch_id} · final odo {(h.final_odometer || 0).toLocaleString("en-IN")} km · fuel {h.fuel_level} · by {h.requested_by_name}</div>
              {h.condition_notes && <div className="text-[11px] text-slate-400 mt-0.5">“{h.condition_notes}”</div>}
            </div>
            <span className="rounded-full bg-fuchsia-100 px-2 py-0.5 text-[9px] font-bold uppercase text-fuchsia-700">to accounts</span>
          </div>
          {h.photos?.length > 0 && <div className="mt-2 flex gap-1.5 overflow-x-auto">{h.photos.map((p, i) => <img key={i} src={p} alt="" className="h-12 w-12 rounded-lg object-cover" />)}</div>}
          {rejecting === h.id ? (
            <div className="mt-3 flex gap-2">
              <input data-testid="handover-reject-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason…" autoFocus
                className="flex-1 rounded-xl border border-red-200 px-3 py-2 text-sm" />
              <button data-testid="handover-reject-confirm" onClick={reject} className="rounded-xl bg-[#BB162B] px-4 text-xs font-bold text-white">Reject</button>
              <button onClick={() => setRejecting(null)} className="rounded-xl border border-slate-200 px-3 text-xs text-slate-500">Cancel</button>
            </div>
          ) : (
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button data-testid={`handover-approve-btn-${h.reg_no}`} onClick={() => approve(h.id)}
                className="flex items-center justify-center gap-1.5 rounded-xl bg-emerald-600 py-3 text-sm font-bold text-white"><Check size={16} /> Approve Handover</button>
              <button data-testid={`handover-reject-btn-${h.reg_no}`} onClick={() => { setRejecting(h.id); setReason(""); }}
                className="flex items-center justify-center gap-1.5 rounded-xl border border-red-300 py-3 text-sm font-bold text-[#BB162B]"><X size={16} /> Reject</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function Approvals() {
  const { user } = useAuth();
  const [view, setView] = useState("today");
  const [items, setItems] = useState([]);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");
  const [fType, setFType] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fKec, setFKec] = useState("");
  const [adds, setAdds] = useState([]);
  const [rejAdd, setRejAdd] = useState(null);
  const [addReason, setAddReason] = useState("");

  const monthStart = new Date().toISOString().slice(0, 7) + "-01";
  const load = () => {
    api.get("/bookings", { params: { date_from: monthStart } }).then((r) => setItems(r.data));
    api.get("/trip-additions", { params: { status: "pending_sm_approval" } }).then((r) => setAdds(r.data)).catch(() => {});
  };
  const approveAdd = async (id) => {
    try { await api.post(`/trip-additions/${id}/approve`); toast.success("Extra drive approved — KEC notified"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const rejectAdd = async () => {
    if (!addReason.trim()) { toast.error("A short reason is required"); return; }
    try { await api.post(`/trip-additions/${rejAdd}/reject`, { reason: addReason }); toast.success("Rejected — KEC notified"); setRejAdd(null); setAddReason(""); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const today = new Date().toISOString().slice(0, 10);
  const pending = items.filter((b) => b.status === "pending_sm_approval");
  const todayItems = items.filter((b) => (b.slot_start || b.created_at).slice(0, 10) === today || b.status === "pending_sm_approval");
  const kecs = useMemo(() => [...new Set(items.map((b) => b.kec_name))].sort(), [items]);
  const monthItems = items.filter((b) => (!fType || b.type === fType) && (!fStatus || b.status === fStatus) && (!fKec || b.kec_name === fKec));

  const approve = async (id) => {
    try { await api.post(`/bookings/${id}/approve`); toast.success("Approved — trip can start now"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const reject = async () => {
    if (!reason.trim()) { toast.error("A short reason is required — it goes back to the KEC"); return; }
    try { await api.post(`/bookings/${rejecting}/reject`, { reason }); toast.success("Rejected — KEC notified"); setRejecting(null); setReason(""); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const Card = ({ b, actions }) => (
    <div data-testid={`approval-card-${b.reg_no}`} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-heading font-bold text-[#0E2240]">{b.reg_no} · {b.model}</div>
          <div className="mt-0.5 text-xs text-slate-500">{b.kec_name} → {b.customer_name}{b.enquiry_id && ` · ENQ ${b.enquiry_id}`}</div>
          {b.additional_model && (
            <div data-testid="additional-model-detail" className="text-[11px] font-semibold text-amber-700">
              Enquiry: {b.enquiry_model || (b.customers ? "per customer" : "—")} → Driving: {b.model}
              {b.cap_warning && <span className="ml-1 font-bold text-[#BB162B]">· CAP EXCEEDED (30-day limit)</span>}
            </div>
          )}
          {b.type === "connected" && b.customers && <div className="text-[11px] text-slate-500">{b.customers.map((c) => c.customer_name).join(", ")} · {b.address}</div>}
          {b.type === "home" && <div className="text-[11px] text-slate-500">📍 {b.address} · <b className="text-[#BB162B]">{b.expected_km} km</b></div>}
          {b.type === "utility" && <div className="text-[11px] text-slate-500">{b.utility_purpose?.replace(/_/g, " ").toUpperCase()}{b.driver_name && ` · taken by ${b.driver_name}`}{b.utility_note && ` · ${b.utility_note}`}</div>}
          <div className="mt-0.5 text-[11px] text-slate-400">Slot {dt(b.slot_start)}{b.approval_tat_min != null && ` · approved in ${b.approval_tat_min} min`}</div>
          {b.rejection_reason && <div className="text-[11px] text-[#BB162B]">Rejected: {b.rejection_reason}</div>}
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase ${typeCls[b.type]}`}>{b.type}</span>
          {b.additional_model && <span data-testid="additional-model-badge" className="rounded-full bg-amber-100 px-2 py-0.5 text-[9px] font-bold uppercase text-amber-700">Additional Model</span>}
          <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase ${statusCls[b.status] || "bg-slate-100 text-slate-500"}`}>{b.status.replace(/_/g, " ")}</span>
        </div>
      </div>
      {actions && (
        rejecting === b.id ? (
          <div className="mt-3 flex gap-2">
            <input data-testid="reject-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (sent to KEC)…" autoFocus
              className="flex-1 rounded-xl border border-red-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#BB162B]" />
            <button data-testid="reject-confirm-btn" onClick={reject} className="rounded-xl bg-[#BB162B] px-4 text-xs font-bold text-white">Reject</button>
            <button onClick={() => setRejecting(null)} className="rounded-xl border border-slate-200 px-3 text-xs text-slate-500">Cancel</button>
          </div>
        ) : (
          <div className="mt-3 grid grid-cols-2 gap-2">
            <button data-testid={`approve-btn-${b.reg_no}`} onClick={() => approve(b.id)}
              className="flex items-center justify-center gap-1.5 rounded-xl bg-emerald-600 py-3 text-sm font-bold text-white active:scale-[0.98] transition-transform duration-150"><Check size={16} /> Approve</button>
            <button data-testid={`reject-btn-${b.reg_no}`} onClick={() => { setRejecting(b.id); setReason(""); }}
              className="flex items-center justify-center gap-1.5 rounded-xl border border-red-300 py-3 text-sm font-bold text-[#BB162B]"><X size={16} /> Reject</button>
          </div>
        )
      )}
    </div>
  );

  const selCls = "rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium";
  const isVP = user.role === "vp";
  const showHandovers = ["vp", "admin"].includes(user.role);
  if (isVP) {
    return (
      <div className="px-4 py-5 lg:px-8 max-w-2xl">
        <h1 className="font-heading text-2xl font-bold text-[#0E2240]">VP Approvals</h1>
        <p className="text-xs text-slate-500 mt-0.5">End-of-life vehicle handovers to Accounts — single tap to approve</p>
        <HandoverApprovals />
      </div>
    );
  }
  return (
    <div className="px-4 py-5 lg:px-8 max-w-2xl">
      <h1 className="font-heading text-2xl font-bold text-[#0E2240]">SM Approval Dashboard</h1>
      <p className="text-xs text-slate-500 mt-0.5">Every test drive needs your approval before it can start · target TAT &lt; 15 min</p>
      {showHandovers && <HandoverApprovals />}
      <div className="mt-4 flex rounded-xl bg-slate-100 p-1">
        {[["today", `Today${pending.length + adds.length ? ` (${pending.length + adds.length} pending)` : ""}`], ["month", "Month Register"]].map(([t, l]) => (
          <button key={t} data-testid={`approvals-view-${t}`} onClick={() => setView(t)}
            className={`flex-1 rounded-lg py-2 text-sm font-bold ${view === t ? "bg-[#0E2240] text-white" : "text-slate-500"}`}>{l}</button>
        ))}
      </div>

      {view === "today" && (
        <div className="mt-4 space-y-3">
          {pending.length === 0 && adds.length === 0 && <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-center text-sm font-semibold text-emerald-700">No pending approvals 🎉</div>}
          {adds.map((a) => (
            <div key={a.id} data-testid={`addition-card-${a.enquiry_id}`} className="rounded-xl border border-purple-200 bg-purple-50 p-4 shadow-sm">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-heading font-bold text-[#0E2240]">{a.reg_no} · {a.model} <span className="ml-1 rounded-full bg-purple-200 px-2 py-0.5 text-[9px] font-bold uppercase text-purple-800">{a.on_spot ? (a.transfer ? "TD during transfer" : "On-spot TD at venue") : "Extra drive mid-trip"}</span></div>
                  <div className="mt-0.5 text-xs text-slate-600">{a.kec_name} wants to drive <b>{a.customer_name}</b> · ENQ {a.enquiry_id} before gate-in</div>
                  {a.additional_model && <div className="text-[11px] font-semibold text-amber-700">Enquiry: {a.enquiry_model} → Driving: {a.model}{a.cap_warning && <span className="ml-1 font-bold text-[#BB162B]">· CAP EXCEEDED</span>}</div>}
                  {a.note && <div className="text-[11px] text-slate-500">Note: {a.note}</div>}
                  <div className="mt-0.5 text-[11px] text-slate-400">Requested {dt(a.created_at)}</div>
                </div>
              </div>
              {rejAdd === a.id ? (
                <div className="mt-3 flex gap-2">
                  <input data-testid="addition-reject-reason" value={addReason} onChange={(e) => setAddReason(e.target.value)} placeholder="Reason (sent to KEC)…" autoFocus className="flex-1 rounded-xl border border-red-200 px-3 py-2 text-sm" />
                  <button data-testid="addition-reject-confirm" onClick={rejectAdd} className="rounded-xl bg-[#BB162B] px-4 text-xs font-bold text-white">Reject</button>
                  <button onClick={() => setRejAdd(null)} className="rounded-xl border border-slate-200 px-3 text-xs text-slate-500">Back</button>
                </div>
              ) : (
                <div className="mt-3 flex gap-2">
                  <button data-testid={`addition-approve-btn-${a.enquiry_id}`} onClick={() => approveAdd(a.id)} className="flex-1 rounded-xl bg-emerald-600 py-2.5 text-sm font-bold text-white">Approve extra drive</button>
                  <button data-testid={`addition-reject-btn-${a.enquiry_id}`} onClick={() => { setRejAdd(a.id); setAddReason(""); }} className="rounded-xl border border-red-200 px-4 py-2.5 text-sm font-bold text-[#BB162B]">Reject</button>
                </div>
              )}
            </div>
          ))}
          {pending.map((b) => <Card key={b.id} b={b} actions />)}
          {todayItems.filter((b) => b.status !== "pending_sm_approval").length > 0 && (
            <h2 className="pt-2 font-heading text-xs font-bold uppercase tracking-widest text-slate-400">Today's other test drives</h2>
          )}
          {todayItems.filter((b) => b.status !== "pending_sm_approval").map((b) => <Card key={b.id} b={b} />)}
        </div>
      )}

      {view === "month" && (
        <>
          <div className="mt-4 flex gap-2 flex-wrap">
            <select data-testid="filter-type" value={fType} onChange={(e) => setFType(e.target.value)} className={selCls}>
              <option value="">All types</option>
              {["showroom", "home", "connected", "utility"].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select data-testid="filter-status" value={fStatus} onChange={(e) => setFStatus(e.target.value)} className={selCls}>
              <option value="">All statuses</option>
              {["pending_sm_approval", "blocked", "approved", "completed", "rejected", "expired"].map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
            </select>
            <select data-testid="filter-kec" value={fKec} onChange={(e) => setFKec(e.target.value)} className={selCls}>
              <option value="">All KECs</option>
              {kecs.map((k) => <option key={k}>{k}</option>)}
            </select>
          </div>
          <div className="mt-3 space-y-2">
            {monthItems.map((b) => <Card key={b.id} b={b} actions={b.status === "pending_sm_approval"} />)}
            {monthItems.length === 0 && <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-400">No test drives match the filters</div>}
          </div>
        </>
      )}
    </div>
  );
}
